import { useState, useEffect } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Clipboard, Check, Rocket, Zap, Code as CodeIcon, FileText, HelpCircle, ListTodo, Menu, Key, Copy } from "lucide-react";

// Reusable CodeBlock Component
const CodeBlock = ({ codeString, language, showLineNumbers = true }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(codeString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative border border-gray-700 pt-2 pb-4 px-4 rounded-lg mt-3 mb-6 shadow-md">
      <div className="flex justify-between items-center mb-1">
        <span className="text-gray-400 text-xs capitalize">{language}</span>
        <button
          className="text-gray-400 hover:text-white transition p-1.5 rounded-md hover:bg-gray-600"
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? <Check size={16} className="text-green-400" /> : <Clipboard size={16} />}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={{
          ...oneDark,
          "pre[class*='language-']": {
            backgroundColor: "transparent",
            padding: "0",
            margin: "0",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          },
          "code[class*='language-']": {
            backgroundColor: "transparent",
            fontSize: "0.875rem",
            lineHeight: "1.5rem",
          },
        }}
        showLineNumbers={showLineNumbers}
        lineNumberStyle={{ color: "#6b7280", fontSize: "0.75rem", paddingRight: "1em", userSelect: "none" }}
        wrapLines={true}
        customStyle={{ margin: "0", padding: "0" }}
      >
        {codeString.trim()}
      </SyntaxHighlighter>
    </div>
  );
};


function Docs() {
  const [activeSection, setActiveSection] = useState('intro'); // Set initial active section to Intro
  const [currentSectionContents, setCurrentSectionContents] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [copiedLLMDocs, setCopiedLLMDocs] = useState(false);

  // Define sections for navigation and content rendering
  const docSections = [
    { id: 'intro', title: 'Introduction', icon: Key },
    { id: 'pageDataObject', title: 'The PageData Object', icon: FileText },
    { id: 'quickstart', title: 'Crawl and Bulk Search', icon: CodeIcon },
    { id: 'apiReference', title: 'API Reference', icon: FileText },
    { id: 'tools', title: 'Data Processing', icon: Rocket }
  ];

  // scroll window to top on tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSection]);

  // Map of section IDs to their internal headings for "Contents"
  const sectionHeadingsMap = {
    intro: [
      { id: 'install-library', title: 'Install the Library' },
      { id: 'get-api-key', title: 'Get Your API Key' },
      { id: 'initialize-client', title: 'Initialize the Client' },
      { id: 'single-search', title: 'Perform a Single Search' },
    ],
    pageDataObject: [
      { id: 'pagedata-intro', title: 'Understanding PageData' },
      { id: 'id-attr', title: 'id' },
      { id: 'url-attr', title: 'url' },
      { id: 'domain-attr', title: 'domain' },
      { id: 'timestamp-attr', title: 'timestamp' },
      { id: 'bulk-search-id-attr', title: 'bulk_search_id' },
      { id: 'search-type-attr', title: 'search_type' },
      { id: 'text-content-attr', title: 'text_content' },
      { id: 'html-content-attr', title: 'html_content' },
      { id: 'internal-links-attr', title: 'internal_links' },
      { id: 'external-links-attr', title: 'external_links' },
      { id: 'latency-ms-attr', title: 'latency_ms' },
      { id: 'complete-attr', title: 'complete' },
      { id: 'created-at-attr', title: 'created_at' },
    ],
    quickstart: [
      { id: 'crawl-domain', title: 'Crawl an Entire Domain' },
      { id: 'initiate-bulk-search', title: 'Initiate and Collect Bulk Search' },
      { id: 'simple-bulk-search', title: 'Simple Bulk Search (for many URLs)' },
    ],
    apiReference: [
      { id: 'init-method', title: '__init__()' },
      { id: 'search-method', title: 'search()' },
      { id: 'crawl-method', title: 'crawl()' },
      { id: 'bulk-search-method', title: 'bulk_search()' },
      { id: 'collect-results-method', title: 'collect_results()' },
      { id: 'simple-bulk-search-method', title: 'simple_bulk_search()' },
      { id: 'list-available-method', title: 'list_available()' },
      { id: 'pull-data-method', title: 'pull_data()' },
    ],
    tools: [
      { id: 'install-tools-library', title: 'Install the Tools Library' },
      { id: 'boilerplate-removal', title: 'Boilerplate removal' },
      { id: 'csv-json-sqlite-storage', title: 'CSV/JSON/Sqlite Storage' },
      { id: 'duplicate-page-removal', title: 'Duplicate Page Removal' },
    ]
  };

  // Effect to update current section contents when activeSection changes
  useEffect(() => {
    setCurrentSectionContents(sectionHeadingsMap[activeSection] || []);
    setIsSidebarOpen(false); // Close sidebar if open when section changes (e.g., on mobile)
  }, [activeSection]);

  // Function to scroll to an element by its ID
  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsSidebarOpen(false); // Close sidebar after clicking a content link on small screens
  };

  // Function to copy LLM docs to clipboard
  const handleCopyLLMDocs = () => {
    navigator.clipboard.writeText(llmDocsContent);
    setCopiedLLMDocs(true);
    setTimeout(() => setCopiedLLMDocs(false), 2000);
  };


  // --- All Code Snippets ---
  const installCommand = `pip install desync_search`;
  const envVarBash = `export DESYNC_API_KEY="YOUR_API_KEY"`;
  const envVarPowerShell = `$env:DESYNC_API_KEY="YOUR_API_KEY"`;
  const initClientEnv = `from desync_search import DesyncClient\n\n# Reads DESYNC_API_KEY from your environment\nclient = DesyncClient()`;
  const initClientDirect = `from desync_search import DesyncClient\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")`;
  const singleSearchCode = `from desync_search import DesyncClient\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\ntarget_url = "https://example.com"\n\nresult = client.search(target_url)\n\nprint(f"URL: {result.url}")\nprint(f"Text Preview: {result.text_content[:100]}")\nprint(f"Found {len(result.internal_links)} internal links.")`;
  const crawlDomainCode = `from desync_search import DesyncClient\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\nstart_url = "https://example.com"\n\npages = client.crawl(\n    start_url=start_url,\n    max_depth=1, # Crawl starting page and its direct links\n    scrape_full_html=False # Set to True to get full HTML content\n)\n\nprint(f"Discovered {len(pages)} pages.")\n\n# Print text content from the first 5 crawled pages\nfor i, page in enumerate(pages[:5]):\n    print(f"\\n--- Page {i+1} ---")\n    print(f"Crawled URL: {page.url}")\n    print(f"Text Preview: {page.text_content[:200]}...")\n`;
  const bulkSearchCode = `from desync_search import DesyncClient\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\nurls = [\n    "https://example.com/page1",\n    "https://example.com/page2",\n    "https://another-domain.com/resource"\n] # Up to 1000 URLs\n\n# ----- Start Bulk Search -----\nbulk_info = client.bulk_search(target_list=urls)\nbulk_search_id = bulk_info.get('bulk_search_id')\nprint(f"Bulk Search ID: {bulk_search_id}")\nprint(f"Total Links Scheduled: {bulk_info.get('total_links')}")\n\n# ----- Collect Bulk Search Results -----\nprint("\\nCollecting results...")\nresults = client.collect_results(\n    bulk_search_id=bulk_search_id,\n    target_links=urls, # Pass the original list of URLs\n    wait_time=90.0, # Max seconds to wait for completion (adjust as needed)\n    completion_fraction=0.95 # Stop when 95% of results are done\n)\n\nprint(f"Retrieved {len(results)} pages from the bulk search.")\n\n# Print text content from the first 5 pages\nfor i, result in enumerate(results[:5]):\n    print(f"\\n--- Page {i+1} ---")\n    print(f"URL: {result.url}")\n    print(f"Text Preview: {result.text_content[:200]}...") # Print first 200 chars\n`;
  const simpleBulkSearchCode = `from desync_search import DesyncClient\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\nmany_urls = [\n    "https://example.com/product/1",\n    "https://example.com/product/2",\n    # ... add thousands of URLs\n    "https://another-site.com/article/abc"\n]\n\n# Handles chunking, polling, and aggregation automatically\nresults = client.simple_bulk_search(\n    target_list=many_urls,\n    wait_time=60.0, # Max wait time per chunk\n    extract_html=False # Set to True to get full HTML content for each page\n)\n\nprint(f"Retrieved {len(results)} pages using simple_bulk_search.")\n\n# Print text content from the first 5 pages\nfor i, result in enumerate(results[:5]):\n    print(f"\\n--- Page {i+1} ---")\n    print(f"URL: {result.url}")\n    print(f"Text Preview: {result.text_content[:200]}...")\n`;
  const initMethodExample = `from desync_search import DesyncClient\n\n# Initialize with API key directly\nclient_direct = DesyncClient(user_api_key="YOUR_ACTUAL_API_KEY")`;
  const searchMethodExample = `result = client.search(\n    url="https://example.com/specific-page",\n    search_type="stealth_search", # or "test_search"\n    scrape_full_html=True # Get the full HTML content\n)\nprint(result.html_content if result.html_content else "No HTML content.")`;
  const crawlMethodExample = `pages = client.crawl(\n    start_url="https://blog.example.com",\n    max_depth=2, # max_depth = 0 is the start_url\n    scrape_full_html=False\n)\nfor page in pages:\n    print(f"Found: {page.url} at depth {getattr(page, 'depth', 'N/A')}")`;
  const bulkSearchMethodExample = `urls_to_search = ["https://example.com/news", "https://example.com/about"]\nbulk_job_details = client.bulk_search(\n    target_list=urls_to_search,\n    extract_html=True\n)\nprint(f"Bulk job ID: {bulk_job_details['bulk_search_id']}")`;
  const listAvailableMethodExample = `# List available records for a specific bulk search\nprevious_records = client.list_available(bulk_search_id="some_bulk_id_123")\nfor record in previous_records:\n    print(f"Record ID: {record.record_id}, URL: {record.url}, Completed: {record.complete}")`;
  const pullDataMethodExample = `# Pull full data for a specific record ID\nrecord_data = client.pull_data(record_id="specific_record_id_abc")\nif record_data:\n    print(record_data[0].text_content)\n\n# Pull full data for a specific URL (if previously fetched)\nurl_data = client.pull_data(url="https://example.com/specific-page")\nif url_data:\n    print(url_data[0].text_content)`;
  const collectResultsMethodExample = `results = client.collect_results(\n    bulk_search_id="your_bulk_id",\n    target_links=["https://url1.com", "https://url2.com"],\n    wait_time=45.0, # Wait up to 45s\n    poll_interval=5.0, # Check every 5s\n    completion_fraction=1.0 # Wait for all results\n)\nprint(f"Collected {len(results)} results.")`;
  const simpleBulkMethodExample = `mega_url_list = ["https://site.com/page1", "... many more ...", "https://site.com/page10000"]\nall_results = client.simple_bulk_search(\n    target_list=mega_url_list,\n    extract_html=False, # Save credits if you only need text/links\n    completion_fraction=0.95 # Consider job done when 95% of each chunk is complete\n)\nprint(f"Total results from simple_bulk_search: {len(all_results)}")`;
  const installDataCommand = `pip install desync-data`;
  const boilerplateRemovalFunctions = `# These functions take a list of the PageData search results from desync_search\n# Then returns a list of cleaned PageData objects (this modifies text_content)\npages = remove_boilerplate_prefix(pages: List[PageData], delimiter: str)\npages = remove_boilerplate_suffix(pages: List[PageData], delimiter: str)`
  const boilerplateRemoval = `from desync_search import DesyncClient\nfrom desync_data.core import remove_boilerplate_prefix, remove_boilerplate_suffix\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\npages = client.crawl(start_url="https://www.137ventures.com", max_depth=2)\n\nprefix_delimiter = "LP LOGIN"\nsuffix_delimiter = "Home\\nPortfolio"\n\n# Delete everything before/including the prefix_delimeter\npages = remove_boilerplate_prefix(pages, prefix_delimiter)\n\n# Delete everything after/including the prefix_delimeter\npages = remove_boilerplate_suffix(pages, suffix_delimeter)\n\n# Print the first and last 100 characters of the first 5 search results\nfor i, page in enumerate(pages[:5]):\n    print(f"\\n--- Page {i+1} ---")\n    print(f"URL: {page.url}")\n    print(page.text_content[:100])\n    print("...")\n    print(page.text_content[-100:])`
  const storageFunctions = `save_to_csv(pages: List[PageData], filepath: str, mode: str = "w")\nsave_to_json(pages: List[PageData], filepath: str, mode: str = "w")\nsave_to_sqlite(pages: List[PageData], db_path: str, table_name: str = "pages", append: bool = True)`
  const storage = `from desync_search import DesyncClient\nfrom desync_data.core import save_to_csv, save_to_json, save_to_sqlite\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\npages = client.crawl(start_url="https://www.137ventures.com", max_depth=2)\n\n# Save the search results in all three formats\nsave_to_csv(pages=pages, filepath="output/output.csv", mode="w")\nsave_to_json(pages=pages, filepath="output/output.json", mode="w")\nsave_to_sqlite(pages=pages, filepath="output/", table_name="pages", append=True)`
  const duplicateRemovalFunction = ` # Outputs a list of PageData objects\npages = remove_duplicate_pages(pages: List[PageData])`
  const duplicateRemoval = `from desync_search import DesyncClient\nfrom desync_data.core import remove_duplicate_pages\n\nclient = DesyncClient(user_api_key="YOUR_API_KEY")\npages = client.crawl(start_url="https://www.137ventures.com", max_depth=2)\n\n# Print the number of pages before cleaning\nprint(f'Number of pages before duplicate removal: {len(pages)}')\n\n# Print number of pages after cleaning\npages = remove_duplicate_pages(pages)\nprint(f'Number of pages after duplicate removal: {len(pages)}')`

  // LLM Documentation content
  const llmDocsContent = `Desync AI Python Library Documentation For Web Scraping

1. Install
pip install desync_search

2. Authentication
You need an API key from the Desync AI dashboard.

Option A – Direct init:
from desync_search import DesyncClient
client = DesyncClient(user_api_key="YOUR_API_KEY")

Option B – Environment variable:
export DESYNC_API_KEY="YOUR_API_KEY"
client = DesyncClient()

3. Single Search (Example)
from desync_search import DesyncClient
client = DesyncClient(user_api_key="YOUR_API_KEY")
result = client.search("https://example.com")
print("URL:", result.url)
print("Text Preview:", result.text_content[:200])
print("Internal Links:", result.internal_links)

4. Crawl (Example)
from desync_search import DesyncClient
client = DesyncClient(user_api_key="YOUR_API_KEY")
pages = client.crawl(start_url="https://example.com", max_depth=1)
print(f"Discovered {len(pages)} pages.")
for i, page in enumerate(pages[:3]):
    print(f"\\nPage {i+1}: {page.url}")
    print(page.text_content[:200])

5. Bulk Search (Example)
from desync_search import DesyncClient
client = DesyncClient(user_api_key="YOUR_API_KEY")
urls = ["https://example.com/about", "https://example.com/contact"]
bulk_info = client.bulk_search(target_list=urls)
bulk_id = bulk_info["bulk_search_id"]
results = client.collect_results(
    bulk_search_id=bulk_id,
    target_links=urls,
    wait_time=60,
    completion_fraction=1.0
)
print(f"Retrieved {len(results)} results.")
for i, page in enumerate(results):
    print(f"\\nPage {i+1}: {page.url}")
    print(page.text_content[:200])

6. PageData Object Attributes
- id: int
- url: str
- domain: str
- timestamp: int
- bulk_search_id: str or None
- search_type: "stealth_search" or "test_search"
- text_content: str
- html_content: str (optional)
- internal_links: list[str]
- external_links: list[str]
- latency_ms: int
- complete: bool
- created_at: int

7. Data Cleaning Tools (requires pip install desync-data)
from desync_data.core import (
    remove_boilerplate_prefix,
    remove_boilerplate_suffix,
    remove_duplicate_pages,
    save_to_csv,
    save_to_json,
    save_to_sqlite
)

pages = remove_boilerplate_prefix(pages, "Start Here")
pages = remove_boilerplate_suffix(pages, "Contact Us")
pages = remove_duplicate_pages(pages)
save_to_csv(pages, "output.csv")
save_to_json(pages, "output.json")
save_to_sqlite(pages, "output/pages.db")

8. Other API Methods
- simple_bulk_search(target_list, extract_html=False)
- list_available(url_list=None, bulk_search_id=None)
- pull_data(record_id=None, url=None)

Example simple_bulk_search:
results = client.simple_bulk_search(
    target_list=[...],
    wait_time=60,
    extract_html=False
)
print(len(results))

Example list_available:
records = client.list_available(bulk_search_id="some_id")
for r in records:
    print(r.url, r.complete)

Example pull_data:
data = client.pull_data(url="https://example.com")
if data:
    print(data[0].text_content)
`;

  // --- End of Code Snippets ---

  const renderSectionContent = () => {
    const currentSection = docSections.find(sec => sec.id === activeSection);
    const SectionIcon = currentSection?.icon || HelpCircle; // Fallback icon

    return (
      <section className="min-w-0"> {/* Add min-w-0 to allow section to shrink */}
        {/* Only show main header on larger screens, or if sidebar is closed on small screens */}
        <header className="mb-12 md:block hidden"> {/* Hide on small screens, show on md and up */}
          <div className="flex items-center justify-between mb-6">
            <div className="inline-flex items-center justify-center bg-yellow-600/10 text-yellow-400 p-3 rounded-lg ring-1 ring-inset ring-yellow-600/20">
              <SectionIcon size={32} /> {/* Use the specific section icon */}
            </div>
            <button
              onClick={handleCopyLLMDocs}
              className="flex items-center space-x-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              aria-label="Copy documentation for LLM"
            >
              {copiedLLMDocs ? (
                <>
                  <Check size={18} className="text-green-300" />
                  <span className="font-medium">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={18} />
                  <span className="font-medium">Copy Docs for LLM</span>
                </>
              )}
            </button>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-gray-50">{currentSection.title}</h1>
          <p className="text-lg sm:text-xl text-gray-400">
            {activeSection === 'intro' && "Learn how to get started with Desync AI by installing the library and managing your API key."}
            {activeSection === 'quickstart' && "Common use cases to get you going immediately."}
            {activeSection === 'apiReference' && `Detailed information about the DesyncClient methods.`}
            {activeSection === 'tools' && "Some useful tools for cleaning and manipulating the data you scrape with desync_search."}
            {activeSection === 'pageDataObject' && "The fundamental object returned by all Desync AI search operations, containing all extracted web page information."}
          </p>
        </header>

        {/* Dynamic content based on activeSection */}
        {activeSection === 'intro' && (
          <>
            <div className="mb-10">
              <h3 id="install-library" className="text-2xl font-semibold mb-3 text-gray-100">1. Install the Library</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Add Desync AI to your Python environment using pip:</p>
              <CodeBlock codeString={installCommand} language="bash" showLineNumbers={false} />
            </div>
            <div className="mb-10">
              <h3 id="get-api-key" className="text-2xl font-semibold mb-3 text-gray-100">2. Get Your API Key</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">You'll need an API key to authenticate your requests. Grab yours from your Desync AI dashboard:</p>
              <p className="text-blue-500 hover:text-blue-400 text-lg leading-relaxed mb-2"><a href="https://beta.desync.ai" target="_blank" rel="noreferrer"><strong>Get your API key here!</strong></a></p>
            </div>
            <div>
              <h3 id="initialize-client" className="text-2xl font-semibold mb-3 text-gray-100">3. Initialize the Client</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">In your Python script, import and initialize the <code>DesyncClient</code>:</p>
              <p className="text-gray-300 text-lg leading-relaxed mb-2"><strong>Option A: Direct Initialization (Recommended for simplicity)</strong></p>
              <CodeBlock codeString={initClientDirect} language="python" />
              <p className="text-gray-300 text-lg leading-relaxed mt-6 mb-2"><strong>Option B: Environment Variable (Recommended for production)</strong></p>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Set the <code>DESYNC_API_KEY</code> environment variable. Replace <code>"YOUR_API_KEY"</code> with your actual key.</p>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">For Bash/Zsh:</p>
              <CodeBlock codeString={envVarBash} language="bash" showLineNumbers={false} />
              <p className="text-gray-300 text-lg leading-relaxed mb-2">For PowerShell:</p>
              <CodeBlock codeString={envVarPowerShell} language="powershell" showLineNumbers={false} />
              <p className="text-gray-300 text-lg leading-relaxed mt-4 mb-2">Then, initialize your client without arguments:</p>
              <CodeBlock codeString={initClientEnv} language="python" />
            </div>
            <div className="mb-10">
              <h3 id="single-search" className="text-2xl font-semibold mb-3 text-gray-100">4. Perform a Single Search</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Fetch content, links, and metadata from a single URL. This returns a <strong>single `PageData` object.</strong></p>
              <CodeBlock codeString={singleSearchCode} language="python" />
            </div>
          </>
        )}

        {activeSection === 'pageDataObject' && (
          <>
            <div className="mb-10">
              <h3 id="pagedata-intro" className="text-2xl font-semibold mb-3 text-gray-100">Understanding the `PageData` Object</h3>
              <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-6 mb-6 shadow-md">
                  <p className="text-blue-200 text-xl font-bold mb-3 flex items-center space-x-3">
                      <Zap size={24} className="text-blue-400" />
                        IMPORTANT: The `PageData` object is fundamental to using Desync AI.
                  </p>
                  <p className="text-blue-200 text-lg leading-relaxed">
                      Every successful search operation, whether it's a single URL lookup, a domain crawl, or a bulk search, returns its results packaged within `PageData` objects.
                  </p>
                  <p className="text-blue-200 text-lg leading-relaxed mt-2">
                      Understanding its attributes is essential for accessing the extracted text, links, and metadata from the web pages you search.
                  </p>
                  <p className="text-blue-200 text-lg leading-relaxed mt-2">
                      * <strong>A single search</strong> returns a <strong>single `PageData` object</strong>. <br />
                      * <strong>Crawl and bulk search</strong> methods return a <strong>list of `PageData` objects.</strong>
                  </p>
              </div>

              <h4 className="text-xl font-semibold mb-4 text-gray-100">PageData Attributes</h4>

              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="id-attr" className="text-lg font-semibold text-blue-300">`id` (int)</h5>
                <p className="text-gray-400">A unique identifier for the search result. This is always an integer.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="url-attr" className="text-lg font-semibold text-blue-300">`url` (str)</h5>
                <p className="text-gray-400">The URL targeted by the search, often referred to as the "target URL" or "target page".</p>
                <p className="text-gray-400 italic">For example: `abc.com/news`.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="domain-attr" className="text-lg font-semibold text-blue-300">`domain` (str)</h5>
                <p className="text-gray-400">The domain of the targeted URL.</p>
                <p className="text-gray-400 italic">For instance, if the URL is `abc.com/news`, then the domain is `abc.com`.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="timestamp-attr" className="text-lg font-semibold text-blue-300">`timestamp` (int)</h5>
                <p className="text-gray-400">A Unix timestamp marking when the result was received. This value is always an integer.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="bulk-search-id-attr" className="text-lg font-semibold text-blue-300">`bulk_search_id` (str)</h5>
                <p className="text-gray-400">A unique identifier for the bulk search batch that this search belongs to. All searches in the same batch share the same `bulk_search_id`. This may be `None` if the search was not part of a bulk search.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="search-type-attr" className="text-lg font-semibold text-blue-300">`search_type` (str)</h5>
                <p className="text-gray-400">A string indicating the type of search performed. Two options are available:</p>
                <ul className="list-disc list-inside text-gray-400 ml-4 mt-2">
                  <li>`stealth_search`: (Default) Renders JavaScript and employs methods to evade most bot detection systems, making the search appear human.</li>
                  <li>`test_search`: Does not render JavaScript and uses minimal measures to appear human. This mode is intended for prototyping and may produce different results than `stealth_search`.</li>
                </ul>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="text-content-attr" className="text-lg font-semibold text-blue-300">`text_content` (str)</h5>
                <p className="text-gray-400">The text extracted from the page’s DOM. This attribute is always a string and is recommended for use with any retrieval or regex-based data extraction.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="html-content-attr" className="text-lg font-semibold text-blue-300">`html_content` (str)</h5>
                <p className="text-gray-400">The full HTML (or as much as was loaded during extraction) of the target page. This attribute is optional and not returned by default to save on bandwidth and processing time. Enable it if you need the complete HTML. Every method that performs a search has an option to turn this on.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="internal-links-attr" className="text-lg font-semibold text-blue-300">`internal_links` (list[str])</h5>
                <p className="text-gray-400">A list of URLs found on the target page that point to the same domain. For example, if the target URL is `abc.com`, the list might include:</p>
                <CodeBlock codeString={`["abc.com/news", "abc.com/faq", ...]`} language="json" showLineNumbers={false} />
                <p className="text-gray-400">This is useful for navigating within the same website.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="external-links-attr" className="text-lg font-semibold text-blue-300">`external_links` (list[str])</h5>
                <p className="text-gray-400">A list of URLs on the target page that point to different domains. For example, if the target URL is `abc.com`, the list might include:</p>
                <CodeBlock codeString={`["def.com", "def.com/news", ...]`} language="json" showLineNumbers={false} />
                <p className="text-gray-400">This is useful for navigating within the same website.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="latency-ms-attr" className="text-lg font-semibold text-blue-300">`latency_ms` (int)</h5>
                <p className="text-gray-400">An integer representing the time in milliseconds (ms) between the server-side initialization of the search and the collection of results. Note that this may differ from client-side timings due to network and polling delays.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="complete-attr" className="text-lg font-semibold text-blue-300">`complete` (bool)</h5>
                <p className="text-gray-400">A boolean value indicating whether the search operation is complete. This is primarily used for developer purposes.</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg border border-gray-700/80 p-6 mb-6">
                <h5 id="created-at-attr" className="text-lg font-semibold text-blue-300">`created_at` (int)</h5>
                <p className="text-gray-400">A Unix timestamp marking the moment the search was initiated on the client-side.</p>
              </div>
            </div>
          </>
        )}

        {activeSection === 'quickstart' && (
          <>
            <div className="mb-10">
              <h3 id="crawl-domain" className="text-2xl font-semibold mb-3 text-gray-100">Crawl an Entire Domain</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Recursively discover and fetch pages starting from a specific URL within the same domain. This returns a <strong>list of `PageData` objects.</strong></p>
              <CodeBlock codeString={crawlDomainCode} language="python" />
            </div>
            <div className="mb-10">
              <h3 id="initiate-bulk-search" className="text-2xl font-semibold mb-3 text-gray-100">Initiate and Collect Bulk Search Results</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Submit a list of up to 1000 URLs for asynchronous processing and then wait to retrieve their results. This returns a <strong>list of `PageData` objects</strong>.</p>
              <CodeBlock codeString={bulkSearchCode} language="python" />
            </div>
            <div>
              <h3 id="simple-bulk-search" className="text-2xl font-semibold mb-3 text-gray-100">Simple Bulk Search (for many URLs)</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">For very large lists of URLs (even over 1000), this method handles splitting into chunks, initiating searches, and aggregating all results seamlessly. This returns a <strong>list of `PageData` objects</strong>.</p>
              <CodeBlock codeString={simpleBulkSearchCode} language="python" />
            </div>
          </>
        )}

        {activeSection === 'apiReference' && (
          <>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="init-method" className="text-xl font-semibold mb-1 text-blue-300"><code>__init__(user_api_key="")</code></h4>
              <p className="text-gray-400 text-md mb-3">Initializes the Desync client.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>user_api_key</code> (str, optional): Your API key. If not provided, reads from <code>DESYNC_API_KEY</code> environment variable.</li>
              </ul>
              <CodeBlock codeString={initMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="search-method" className="text-xl font-semibold mb-1 text-blue-300"><code>search(url, search_type="stealth_search", ...)</code></h4>
              <p className="text-gray-400 text-md mb-3">Performs a search on a single URL.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>url</code> (str): The URL to scrape.</li>
                <li><code>search_type</code> (str, optional): <code>"stealth_search"</code> (10 credits) or <code>"test_search"</code> (1 credit). Default: <code>"stealth_search"</code>.</li>
                <li><code>scrape_full_html</code> (bool, optional): If <code>True</code>, returns full HTML. Default: <code>False</code>.</li>
                <li><code>remove_link_duplicates</code> (bool, optional): If <code>True</code>, removes duplicate links. Default: <code>True</code>.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A single <code>PageData</code> object with page content, links, etc.</p>
              <CodeBlock codeString={searchMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="crawl-method" className="text-xl font-semibold mb-1 text-blue-300"><code>crawl(start_url, max_depth=2, ...)</code></h4>
              <p className="text-gray-400 text-md mb-3">Recursively crawls a website starting from <code>start_url</code>.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>start_url</code> (str): The URL to begin crawling from.</li>
                <li><code>max_depth</code> (int, optional): How many levels deep to crawl. `0` is `start_url` only. Default: `2`.</li>
                <li><code>scrape_full_html</code> (bool, optional): Default: <code>False</code>.</li>
                <li><code>remove_link_duplicates</code> (bool, optional): Default: <code>True</code>.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A list of <code>PageData</code> objects.</p>
              <CodeBlock codeString={crawlMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="bulk-search-method" className="text-xl font-semibold mb-1 text-blue-300"><code>bulk_search(target_list, extract_html=False)</code></h4>
              <p className="text-gray-400 text-md mb-3">Initiates an asynchronous search for up to 1000 URLs. This method returns a job ID that you then use with `collect_results()` to retrieve the `PageData` objects.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>target_list</code> (list[str]): List of URLs.</li>
                <li><code>extract_html</code> (bool, optional): If <code>True</code>, results will include full HTML. Default: <code>False</code>.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A dictionary with <code>bulk_search_id</code> and other metadata. You will use this ID to collect a list of <code>PageData</code> objects later.</p>
              <CodeBlock codeString={bulkSearchMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="collect-results-method" className="text-xl font-semibold mb-1 text-blue-300"><code>collect_results(bulk_search_id, target_links, ...)</code></h4>
              <p className="text-gray-400 text-md mb-3">Polls for and retrieves the results of a bulk search job.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>bulk_search_id</code> (str): The ID obtained from `bulk_search()`.</li>
                <li><code>target_links</code> (list[str]): The list of URLs that were part of that bulk job.</li>
                <li><code>wait_time</code> (float, optional): Max seconds to wait. Default: <code>30.0</code>.</li>
                <li><code>poll_interval</code> (float, optional): Seconds between status checks. Default: <code>2.0</code>.</li>
                <li><code>completion_fraction</code> (float, optional): Fraction (0.0-1.0) of results completion to stop polling. Default: <code>0.975</code>.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A list of <code>PageData</code> objects corresponding to the completed searches.</p>
              <CodeBlock codeString={collectResultsMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="simple-bulk-search-method" className="text-xl font-semibold mb-1 text-blue-300"><code>simple_bulk_search(target_list, ...)</code></h4>
              <p className="text-gray-400 text-md mb-3">Handles large lists of URLs by auto-chunking, initiating searches, and aggregating all results seamlessly. This is a convenient wrapper around `bulk_search()` and `collect_results()`.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>target_list</code> (list[str]): Potentially very large list of URLs.</li>
                <li><code>extract_html</code> (bool, optional): Default: <code>False</code>.</li>
                <li><code>poll_interval</code> (float, optional): Polling interval per chunk. Default: <code>2.0</code>.</li>
                <li><code>wait_time</code> (float, optional): Max wait time per chunk. Default: <code>30.0</code>.</li>
                <li><code>completion_fraction</code> (float, optional): Completion fraction per chunk. Default: <code>1.0</code>.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A list of <code>PageData</code> objects containing all results.</p>
              <CodeBlock codeString={simpleBulkMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="list-available-method" className="text-xl font-semibold mb-1 text-blue-300"><code>list_available(url_list=None, bulk_search_id=None)</code></h4>
              <p className="text-gray-400 text-md mb-3">Retrieves minimal data about previously fetched records (IDs, URLs, timestamps, completion status). These are partial `PageData` objects, primarily for status checking.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong></p>
              <ul className="list-disc list-inside text-gray-300 mb-3 pl-4">
                <li><code>url_list</code> (list[str], optional): Filter by specific URLs.</li>
                <li><code>bulk_search_id</code> (str, optional): Filter by a bulk search ID.</li>
              </ul>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A list of partial <code>PageData</code> objects.</p>
              <CodeBlock codeString={listAvailableMethodExample} language="python" />
            </div>
            <div className="mb-10 p-6 bg-[#1A1A1A] rounded-lg border border-gray-700/80 shadow-lg">
              <h4 id="pull-data-method" className="text-xl font-semibold mb-1 text-blue-300"><code>pull_data(record_id=None, url=None, ...)</code></h4>
              <p className="text-gray-400 text-md mb-3">Retrieves full data for one or more records matching provided filters. This returns complete `PageData` objects for records that have already been fetched.</p>
              <p className="text-gray-300 mb-1"><strong>Parameters:</strong> Can filter by any combination of fields like <code>record_id</code>, <code>url</code>, <code>domain</code>, <code>timestamp</code>, <code>bulk_search_id</code>, <code>search_type</code>, <code>complete</code>, etc.</p>
              <p className="text-gray-300 mb-1"><strong>Returns:</strong> A list of full <code>PageData</code> objects.</p>
              <CodeBlock codeString={pullDataMethodExample} language="python" />
            </div>
          </>
        )}

        {activeSection === 'tools' && (
          <>
            <div className="mb-10">
              <h3 id="install-tools-library" className="text-2xl font-semibold mb-3 text-gray-100">1. Install the Tools Library</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Add Desync Data to your Python environment using pip:</p>
              <CodeBlock codeString={installDataCommand} language="bash" showLineNumbers={false} />
            </div>
            <div>
              <h3 id="boilerplate-removal" className="text-2xl font-semibold mb-3 text-gray-100">2. Boilerplate removal</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Functions to remove boilerplate text. These are used to remove repeated content such as text from navbars/footers. <code>remove_boilerplate_prefix</code> removes any text from each of the pages before and including the delimeter string. <code>remove_boilerplate_suffix</code> removes any text from each of the pages after and including the delimeter string. They both take a list of search results (a list of PageData objects) and a string delimeter as input. They then output a list of PageData objects with the cleaned text_content</p>
              <CodeBlock codeString={boilerplateRemovalFunctions} language="python" />
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Example usage:</p>
              <CodeBlock codeString={boilerplateRemoval} language="python" />
            </div>
            <div>
              <h3 id="csv-json-sqlite-storage" className="text-2xl font-semibold mb-3 text-gray-100">3. CSV/JSON/Sqlite Storage</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Functions to save your search results in different formats. <code>save_to_csv</code>, <code>save_to_json</code>, and <code>save_to_sqlite</code> saves your search results in their respective formats.</p>
              <CodeBlock codeString={storageFunctions} language="python" />
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Example usage:</p>
              <CodeBlock codeString={storage} language="python" />
            </div>
            <div>
              <h3 id="duplicate-page-removal" className="text-2xl font-semibold mb-3 text-gray-100">4. Duplicate Page Removal</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Functions to remove duplicate pages from your results. <code>remove_duplicate_pages</code> takes a list of search results (a list of PageData objects) as input, and outputs a list removing all duplicate pages from the list (leaving one copy still in the results).</p>
              <CodeBlock codeString={duplicateRemovalFunction} language="python" />
              <p className="text-gray-300 text-lg leading-relaxed mb-2">Example usage:</p>
              <CodeBlock codeString={duplicateRemoval} language="python" />
            </div>
          </>
        )}
      </section>
    );
  };

  return (
    <div className="text-white min-h-screen flex flex-col md:flex-row overflow-x-hidden"> {/* Added overflow-x-hidden here */}
      {/* Mobile Header/Menu Button */}
      <header className="md:hidden flex items-center justify-between p-4 bg-gray-800 border-b border-gray-700">
        <h1 className="text-xl font-bold text-gray-50">{docSections.find(sec => sec.id === activeSection)?.title}</h1>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="text-gray-400 hover:text-white p-2 rounded-md hover:bg-gray-700"
          aria-label="Toggle sidebar"
        >
          <Menu size={24} />
        </button>
      </header>

      {/* Left Sidebar Navigation (Conditional Visibility) */}
      <aside className={`
        fixed inset-0 z-40 bg-gray-900 md:bg-transparent md:fixed md:top-0 md:left-0 md:w-72 md:flex-shrink-0
        md:block
        h-full md:h-[calc(100vh-80px)] md:overflow-y-auto p-6 md:pt-6 md:mt-[7.5%]
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0 pt-5' : '-translate-x-full md:translate-x-0 pt-5'} 
        ${isSidebarOpen ? 'mt-[5%]' : 'md:mt-[2%]'} {/* Added for small screens only when open */}
      `}>
        <nav>
          <ul>
            {docSections.map(section => (
              <li key={section.id} className="mb-1.5">
                <button
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-3.5 py-2.5 rounded-lg text-left text-[15px] font-medium transition-all duration-200 ease-in-out transform hover:scale-[1.02]
                    ${activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-gray-200'
                    }`}
                >
                  {section.icon && <section.icon size={19} className="opacity-90" />}
                  <span>{section.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="pt-8 mt-auto text-xs text-gray-500">
          <p>Please reach out to us if you have any issues or if we can help out! <br /><br />jackson@desync.ai</p>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 p-8 md:p-10 lg:p-12 md:ml-72 md:mr-72 max-w-full min-w-0"> {/* Adjusted margins for fixed sidebars */}
        {/* Mobile header with Copy button */}
        <div className="md:hidden mb-6">
          <h1 className="text-3xl font-bold mb-4 text-gray-50">
            {docSections.find(sec => sec.id === activeSection)?.title}
          </h1>
          <button
            onClick={handleCopyLLMDocs}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-md text-sm"
            aria-label="Copy documentation for LLM"
          >
            {copiedLLMDocs ? (
              <>
                <Check size={16} className="text-green-300" />
                <span className="font-medium">Copied!</span>
              </>
            ) : (
              <>
                <Copy size={16} />
                <span className="font-medium">Copy Docs for LLM</span>
              </>
            )}
          </button>
        </div>

        {renderSectionContent()}
      </main>

      {/* Right Sidebar for Contents */}
      {currentSectionContents.length > 0 && (
        <aside className={`
          fixed inset-y-0 right-0 z-40 bg-gray-900 md:bg-transparent md:fixed md:top-0 md:right-0 md:w-72 md:flex-shrink-0
          md:block
          h-full md:h-[calc(100vh-80px)] md:overflow-y-auto p-6 md:pt-6 md:mt-[5%]
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-full' : 'translate-x-0'} md:translate-x-0
          hidden md:block {/* Hide on small screens, show on md and up */}
        `}>
          <div className="mt-8 pt-4 border-t border-gray-700">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center space-x-2">
              <ListTodo size={16} />
              <span>Contents</span>
            </h3>
            <ul>
              {currentSectionContents.map((item) => (
                <li key={item.id} className="mb-1">
                  <button
                    onClick={() => scrollToHeading(item.id)}
                    className="w-full text-left text-gray-400 hover:text-gray-200 transition-colors duration-200 pl-4 py-1.5 text-sm"
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}
    </div>
  );
}

export default Docs;